import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "heroCarousel", "heroImage", 
    "lightbox", "lightboxCarousel", "lightboxImage", "lightboxCounter", 
    "zoomLabel", "zoomInBtn", "zoomOutBtn", "zoomIcon",
    "title", "counter", "loader", "thumbnail", "carousel"
  ]
  static value = { galleryId: String }

  declare readonly heroCarouselTarget: HTMLElement
  declare readonly heroImageTargets: HTMLElement[]
  declare readonly lightboxTarget: HTMLElement
  declare readonly lightboxCarouselTarget: HTMLElement
  declare readonly lightboxImageTargets: HTMLElement[]
  declare readonly lightboxCounterTarget: HTMLElement
  declare readonly hasLightboxCounterTarget: boolean
  declare readonly zoomLabelTarget: HTMLElement
  declare readonly hasZoomLabelTarget: boolean
  declare readonly zoomInBtnTarget: HTMLButtonElement
  declare readonly hasZoomInBtnTarget: boolean
  declare readonly zoomOutBtnTarget: HTMLButtonElement
  declare readonly hasZoomOutBtnTarget: boolean
  declare readonly titleTarget: HTMLElement
  declare readonly hasTitleTarget: boolean
  declare readonly counterTarget: HTMLElement
  declare readonly hasCounterTarget: boolean
  declare readonly thumbnailTargets: HTMLElement[]
  declare readonly carouselTarget: HTMLElement
  
  private images: Array<{ title: string, index: number }> = []
  private currentIndex: number = 0
  private isSyncing: boolean = false
  private zoomLevel: number = 1 // 1, 2, 4
  private zoomDirection: number = 1 // 1 for increasing, -1 for decreasing
  
  // Panning State
  private isPanning: boolean = false
  private startX: number = 0
  private startY: number = 0
  private translateX: number = 0
  private translateY: number = 0

  connect() {
    this.collectImages()
    this.updateUI()
  }

  collectImages() {
    this.images = this.thumbnailTargets.map((item, index) => ({
      title: (item as HTMLElement).dataset.galleryTitle || "",
      index: index
    }))
  }

  // --- Main Gallery Actions ---

  select(event: MouseEvent) {
    event.preventDefault()
    const clickedElement = event.currentTarget as HTMLElement
    this.currentIndex = parseInt(clickedElement.dataset.galleryIndex || "0")
    this.syncAllViews()
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.resetZoom()
    this.syncAllViews()
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.resetZoom()
    this.syncAllViews()
  }

  onHeroScroll() {
    if (this.isSyncing) return
    const newIndex = this.calculateIndexFromScroll(this.heroCarouselTarget)
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex
      this.updateUI()
    }
  }

  // --- Lightbox Actions ---

  openLightbox(event: MouseEvent) {
    if (event) event.preventDefault()
    this.lightboxTarget.classList.remove("hidden")
    setTimeout(() => {
      this.lightboxTarget.classList.remove("opacity-0")
      this.syncLightboxView(false)
      this.updateZoomUI()
    }, 10)
    document.body.style.overflow = "hidden"
  }

  closeLightbox() {
    this.resetZoom()
    this.lightboxTarget.classList.add("opacity-0")
    setTimeout(() => {
      this.lightboxTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }, 500)
  }

  nextLightbox() {
    this.resetZoom()
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.syncAllViews()
  }

  prevLightbox() {
    this.resetZoom()
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.syncAllViews()
  }

  onLightboxScroll() {
    if (this.isSyncing || this.zoomLevel > 1) return
    const newIndex = this.calculateIndexFromScroll(this.lightboxCarouselTarget)
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex
      this.updateUI()
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (this.lightboxTarget.classList.contains("hidden")) return
    
    if (event.key === "Escape") this.closeLightbox()
    if (event.key === "ArrowRight") this.nextLightbox()
    if (event.key === "ArrowLeft") this.prevLightbox()
    if (event.key === "+") this.zoomIn()
    if (event.key === "-") this.zoomOut()
  }

  // --- Mirror Interaction Engine ---

  handleImageClick(event: MouseEvent) {
    if (event) event.stopPropagation()
    
    // Cycle: 1 -> 2 -> 4 -> 2 -> 1
    if (this.zoomLevel === 1) {
      this.zoomDirection = 1
      this.zoomIn()
    } else if (this.zoomLevel === 4) {
      this.zoomDirection = -1
      this.zoomOut()
    } else {
      // We are at 2x. Follow direction.
      if (this.zoomDirection === 1) this.zoomIn()
      else this.zoomOut()
    }
  }

  zoomIn(event?: MouseEvent) {
    if (event) event.stopPropagation()
    const currentImage = this.lightboxImageTargets[this.currentIndex]?.querySelector('img')
    if (!currentImage) return

    if (this.zoomLevel === 1) this.zoomLevel = 2
    else if (this.zoomLevel === 2) this.zoomLevel = 4
    
    this.applyZoom(currentImage)
  }

  zoomOut(event?: MouseEvent) {
    if (event) event.stopPropagation()
    const currentImage = this.lightboxImageTargets[this.currentIndex]?.querySelector('img')
    if (!currentImage) return

    if (this.zoomLevel === 4) this.zoomLevel = 2
    else if (this.zoomLevel === 2) this.zoomLevel = 1
    
    this.applyZoom(currentImage)
  }

  resetZoom(event?: MouseEvent) {
    if (event) event.stopPropagation()
    if (this.zoomLevel === 1) return
    
    this.zoomLevel = 1
    this.zoomDirection = 1
    this.translateX = 0
    this.translateY = 0
    
    const currentImage = this.lightboxImageTargets[this.currentIndex]?.querySelector('img')
    if (currentImage) {
      this.applyZoom(currentImage)
    }
    this.updateZoomUI()
  }

  private applyZoom(img: HTMLImageElement) {
    if (this.zoomLevel > 1) {
      img.style.transform = `scale(${this.zoomLevel}) translate(${this.translateX / this.zoomLevel}px, ${this.translateY / this.zoomLevel}px)`
      this.lightboxCarouselTarget.style.overflowX = 'hidden'
    } else {
      img.style.transform = ''
      this.lightboxCarouselTarget.style.overflowX = 'auto'
    }
    this.updateZoomUI()
  }

  // --- Panning Interaction ---

  startPan(event: MouseEvent | TouchEvent) {
    if (this.zoomLevel === 1) return
    this.isPanning = true
    
    const point = this.getPoint(event)
    this.startX = point.x - this.translateX
    this.startY = point.y - this.translateY
    
    const img = event.currentTarget as HTMLImageElement
    img.classList.add('cursor-grabbing')
  }

  pan(event: MouseEvent | TouchEvent) {
    if (!this.isPanning || this.zoomLevel === 1) return
    event.preventDefault()

    const point = this.getPoint(event)
    const newX = point.x - this.startX
    const newY = point.y - this.startY

    const img = event.currentTarget as HTMLImageElement
    const bounds = this.calculateBounds(img)
    
    this.translateX = Math.max(Math.min(newX, bounds.maxX), bounds.minX)
    this.translateY = Math.max(Math.min(newY, bounds.maxY), bounds.minY)

    this.applyZoom(img)
  }

  stopPan(event: MouseEvent | TouchEvent) {
    this.isPanning = false
    const img = event.currentTarget as HTMLImageElement
    if (img && img.classList) {
        img.classList.remove('cursor-grabbing')
    }
  }

  private getPoint(event: MouseEvent | TouchEvent) {
    if (window.TouchEvent && event instanceof TouchEvent) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY }
    }
    return { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY }
  }

  private calculateBounds(img: HTMLImageElement) {
    const rect = img.getBoundingClientRect()
    const parent = img.parentElement?.getBoundingClientRect()
    if (!parent) return { minX: 0, maxX: 0, minY: 0, maxY: 0 }

    const excessX = (rect.width - parent.width) / 2
    const excessY = (rect.height - parent.height) / 2

    return {
      minX: -excessX,
      maxX: excessX,
      minY: -excessY,
      maxY: excessY
    }
  }

  private updateZoomUI() {
    // Labels
    if (this.hasZoomLabelTarget) {
      this.zoomLabelTarget.textContent = `${this.zoomLevel}x`
      this.zoomLabelTarget.classList.toggle('text-primary', this.zoomLevel > 1)
      this.zoomLabelTarget.classList.toggle('text-white/40', this.zoomLevel === 1)
    }

    // Buttons
    if (this.hasZoomInBtnTarget) this.zoomInBtnTarget.disabled = this.zoomLevel === 4
    if (this.hasZoomOutBtnTarget) this.zoomOutBtnTarget.disabled = this.zoomLevel === 1

    // Predictive Cursor logic
    this.lightboxImageTargets.forEach(target => {
      const img = target.querySelector('img')
      if (!img) return

      // If at 1x or 2x and still zooming in, SHOW PLUS
      // If at 4x or 2x and zooming out, SHOW MINUS
      const shouldZoomOut = (this.zoomLevel === 4) || (this.zoomLevel === 2 && this.zoomDirection === -1)
      
      img.classList.toggle('cursor-zoom-in', !shouldZoomOut)
      img.classList.toggle('cursor-zoom-out', shouldZoomOut && !this.isPanning)
    })
  }

  // --- Synchronization Core ---

  private syncAllViews() {
    this.isSyncing = true
    this.syncHeroView()
    this.syncLightboxView()
    this.updateUI()
    
    setTimeout(() => { this.isSyncing = false }, 500)
  }

  private syncHeroView(smooth = true) {
    const target = this.heroImageTargets[this.currentIndex]
    if (target) {
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest', inline: 'center' })
    }
  }

  private syncLightboxView(smooth = true) {
    if (this.lightboxTarget.classList.contains("hidden")) return
    const target = this.lightboxImageTargets[this.currentIndex]
    if (target) {
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest', inline: 'center' })
    }
  }

  updateUI() {
    const image = this.images[this.currentIndex]
    if (!image) return

    if (this.hasTitleTarget) this.titleTarget.textContent = image.title
    if (this.hasCounterTarget) this.counterTarget.textContent = `${this.currentIndex + 1} / ${this.images.length}`
    if (this.hasLightboxCounterTarget) this.lightboxCounterTarget.textContent = `${this.currentIndex + 1} / ${this.images.length}`
    
    this.thumbnailTargets.forEach((thumb, idx) => {
      const img = thumb.querySelector('img')
      if (idx === this.currentIndex) {
        thumb.classList.add('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
        if (img) img.classList.remove('grayscale', 'opacity-60')
        this.scrollToThumbnail(thumb)
      } else {
        thumb.classList.remove('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
        if (img) img.classList.add('grayscale', 'opacity-60')
      }
    })
  }

  private scrollToThumbnail(thumb: HTMLElement) {
    const carouselWidth = this.carouselTarget.clientWidth
    const thumbLeft = thumb.offsetLeft
    const thumbWidth = thumb.clientWidth
    const targetX = thumbLeft - (carouselWidth / 2) + (thumbWidth / 2)
    
    this.carouselTarget.scrollTo({ left: targetX, behavior: 'smooth' })
  }

  private calculateIndexFromScroll(container: HTMLElement) {
    const scrollLeft = container.scrollLeft
    const width = container.clientWidth
    return Math.round(scrollLeft / width) || 0
  }

  scrollThumbNext() {
    this.carouselTarget.scrollBy({ left: this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }

  scrollThumbPrev() {
    this.carouselTarget.scrollBy({ left: -this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }
}
